import { Box, Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { CustomDialog } from "../../../../../components/customDialog/CustomDialog";
import DataGridGenerico from '../../../../../components/dataGrid/DataGridGenerico';
import { generateColumnsDefaultConfiguration } from '../../../../../components/dataGrid/columnHelpers';
import { TextInput } from '../../../../../components/textInput/TextInput';

const esasDisplayName = {
    dolor: 'Dolor',
    disnea: 'Disnea',
    nauseas: 'Nauseas',
    depresion: 'Depresión',
    ansiedad: 'Ansiedad',
    anorexia: 'Anorexia',
    bienestar: 'Bienestar',
    cansancio: 'Cansancio',
    insomnio: 'Insomnio',
    somnolencia: 'Somnolencia',
    constipacion: 'Constipación',
    result: 'Resultado',
};

const buildEsasValuesFromData = (data) => ({
    dolor: data.dolor || 0,
    disnea: data.disnea || 0,
    nauseas: data.nauseas || 0,
    depresion: data.depresion || 0,
    ansiedad: data.ansiedad || 0,
    anorexia: data.anorexia || 0,
    bienestar: data.bienestar || 0,
    cansancio: data.cansancio || 0,
    insomnio: data.insomnio || 0,
    somnolencia: data.somnolencia || 0,
    constipacion: data.constipacion || 0,
    result: 0
});

export const CalculadoraESAS = ({
    variant,
    handleChange,
    input,
    prevResult,
    data,
    disabledModal = false,
}) => {
    const [esasValues, setEsasValues] = useState(buildEsasValuesFromData(data));

    const [openModal, setOpenModal] = useState(false);
    const esasArray = Object.entries(esasValues).map(([name, value]) => ({ displayName: esasDisplayName[name], name, value }));
    const [result, setResult] = useState(prevResult || 0);

    const handleChangeProp = (name, value) => {
        if (value > 10 || value < 0) return;

        value = isNaN(value) ? 0 : value;
        setEsasValues((prevValues) => ({ ...prevValues, [name]: value }));
    };

    const handleOpenModal = () => setOpenModal(true);

    const handleCloseModal = () => setOpenModal(false);

    // esasVisita: resincroniza esasValues cada vez que cambian los campos de "data".
    useEffect(() => {
        if (variant !== 'esasVisita') return;

        setEsasValues(buildEsasValuesFromData(data));
    }, [variant, data]);

    // esasRI: sincroniza result con prevResult.
    useEffect(() => {
        if (variant !== 'esasRI') return;

        setResult(prevResult || 0);
    }, [variant, prevResult]);

    // esasVisita: sincroniza result con prevResult.
    useEffect(() => {
        if (variant !== 'esasVisita') return;

        setResult(prevResult || 0);
    }, [variant, prevResult]);

    // esasRI: carga esasValues persistidos en localStorage al montar.
    useEffect(() => {
        if (variant !== 'esasRI') return;

        const storedEsasValues = localStorage.getItem('esasValues');
        if (storedEsasValues) {
            const esasValuesParse = JSON.parse(storedEsasValues);
            setEsasValues((prevDatos) => ({
                ...prevDatos,
                ...esasValuesParse,
            }));
        }
    }, [variant]);

    // esasRI: persiste esasValues en localStorage en cada cambio.
    useEffect(() => {
        if (variant !== 'esasRI') return;

        localStorage.setItem('esasValues', JSON.stringify(esasValues));
    }, [variant, esasValues]);

    const columns = generateColumnsDefaultConfiguration([
        {
            headerName: 'Item a evaluar',
            noPaddingHeader: true,
            field: 'displayName',
            flex: 0.6
        },
        {
            headerName: 'Valor',
            field: 'action',
            flex: 0.5,
            renderCell: (cellProps) => {
                const { row } = cellProps;
                return (
                    <TextInput
                        variant='outlined'
                        name={row.name}
                        required={true}
                        disabled={row.name === 'result'}
                        type='number'
                        value={row.name === 'result' ? result : esasValues[row.name]}
                        onChange={(e) => row.name === 'result' ? null : handleChangeProp(row.name, parseInt(e.target.value))}
                        inputProps={{
                            inputMode: 'numeric',
                            min: 0,
                            max: 10,
                        }}
                    />
                )
            }
        },
    ]);

    const calculateESAS = () => {
        if (Object.values(esasValues).every((value) => value !== '')) {
            const esasValuesWithoutResult = { ...esasValues };
            delete esasValuesWithoutResult.result;
            const totalScore = Object.values(esasValuesWithoutResult).reduce((sum, value) => sum + parseInt(value), 0);
            return totalScore;
        } else {
            toast.error("Complete todos los campos!");
            return 0;
        }
    };

    // El nombre del campo final entregado a handleChange difería entre variantes.
    const resultadoFieldName = variant === 'esasRI' ? 'resultadoAduESAS' : 'resultadoESAS';

    const handleAccept = () => {
        for (const [key, value] of Object.entries(esasValues)) {
            if (key === 'result') continue;
            const numericValue = parseInt(value, 10);
            if (Number.isNaN(numericValue) || numericValue < 0 || numericValue > 10) {
                toast.error('Todos los valores deben estar entre 0 y 10. Revise los campos.');
                return;
            }
        }

        const totalESAS = calculateESAS();
        setResult(totalESAS);
        handleChange('dolor', esasValues?.dolor);
        handleChange('disnea', esasValues?.disnea);
        handleChange('nauseas', esasValues?.nauseas);
        handleChange('depresion', esasValues?.depresion);
        handleChange('ansiedad', esasValues?.ansiedad);
        handleChange('anorexia', esasValues?.anorexia);
        handleChange('bienestar', esasValues?.bienestar);
        handleChange('cansancio', esasValues?.cansancio);
        handleChange('insomnio', esasValues?.insomnio);
        handleChange('somnolencia', esasValues?.somnolencia);
        handleChange('constipacion', esasValues?.constipacion);
        handleChange(resultadoFieldName, totalESAS);
        handleCloseModal();
    }

    return (
        <>
            <Grid sx={{ marginBottom: 2.5, marginTop: 2.5 }} size={{ xs: 12 }}>
                <TextInput
                    label={input.displayName}
                    variant='outlined'
                    name={input.name}
                    value={result}
                    required={true}
                    disabled={disabledModal}
                    onClick={() => handleOpenModal()}
                />
            </Grid>
            <CustomDialog
                maxWidth='md'
                fullWidth
                onCancel={() => handleCloseModal()}
                cancelText='Cerrar'
                open={openModal}
                onAccept={handleAccept}
                acceptText='Guardar Calculo'
                title='Calculadora ESAS'
            >
                <Box sx={{ margin: '10px' }}>
                    <p>Complete los valores en cada campo.</p>
                    <p>Siendo <span style={{ fontWeight: 'bold' }}>0</span> = Ausencia y <span style={{ fontWeight: 'bold' }}>10</span> = Máximo.</p>
                </Box>
                <Box sx={{ backgroundColor: '#F5F5F5', borderRadius: '10px', margin: '5px', padding: '5px' }}>
                    <p>Ejemplo:</p>
                    <p><span style={{ fontWeight: 'bold' }}>Dolor 10</span> (mucho dolor) / <span style={{ fontWeight: 'bold' }}>Dolor 0</span> (sin dolor)</p>
                </Box>
                <Box sx={{ width: '100%' }}>
                    <DataGridGenerico
                        columns={columns}
                        rows={esasArray}
                        toolbar={false}
                        hideFooter
                        pageSize={12}
                    />
                </Box>
            </CustomDialog>
        </>
    );
};

export default CalculadoraESAS;